import { useEffect, useRef, useState, type FormEvent } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { PasswordField } from '../../components/PasswordField'
import type { AdminSession } from '../admin/AdminShell'
import { resolveApiBaseUrl } from '../../lib/api-base-url'
import { catalogLabel } from '../../lib/catalog-label'
import { useEscapeClose } from '../../lib/use-escape-close'

type CollaboratorScope = 'active' | 'trash'
type TrashTab = 'users' | 'roles' | 'permissions' | 'spaces'
type DrawerMode = 'create' | 'edit' | 'view'
type SortKey = 'name' | 'email' | 'roleSlug' | 'status' | 'updatedAt' | 'deletedAt'
type TrashResourceKey = Exclude<TrashTab, 'users'>

type CollaboratorItem = {
  id: string
  name: string
  email: string
  roleSlug: string
  role: {
    slug: string
    label: string
  } | null
  status: string
  createdAt: string
  updatedAt: string
  deletedAt: string | null
  deletedBy: {
    id: string
    name: string
    email: string
  } | null
  organization: {
    id: string
    name: string
    slug: string
    status: string
  } | null
  hasDevicePin: boolean
  maxDailyJourneys: number
  permissionsCount: number
  rulesCount: number
}

type CollaboratorsPayload = {
  items: CollaboratorItem[]
  stats: {
    total: number
    active: number
    inactive: number
    trash: number
    roles: number
    trashResources?: {
      collaborators: number
      spaces: number
      roles: number
      permissions: number
    }
  }
  catalog: {
    roles: Array<{
      slug: string
      label: string
    }>
  }
}

type TrashResourceItem = {
  id: string
  name?: string
  label?: string
  slug?: string
  description?: string
  type?: string
  status: string
  updatedAt: string
  deletedAt: string | null
}

type TrashResourcePayload = {
  items: TrashResourceItem[]
}

type CollaboratorForm = {
  name: string
  email: string
  roleSlug: string
  status: string
  maxDailyJourneys: number
  password: string
  confirmPassword: string
}

type PageTone = 'idle' | 'success' | 'error' | 'warning'
type MenuDirection = 'up' | 'down'
type OpenMenuState = {
  id: string
  direction: MenuDirection
  coords: {
    top: number
    left: number
    right: number
    bottom: number
  }
} | null

type CollaboratorsPageProps = {
  session: AdminSession
  scope: CollaboratorScope
  initialCollaboratorId?: string | null
  onInitialCollaboratorOpen?: () => void
}

const apiBaseUrl = resolveApiBaseUrl()
const ROWS_PER_PAGE = 10

const emptyForm: CollaboratorForm = {
  name: '',
  email: '',
  roleSlug: '',
  status: 'active',
  maxDailyJourneys: 1,
  password: '',
  confirmPassword: '',
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value))
}

function formatDateOnly(value: string | null) {
  if (!value) {
    return '—'
  }

  return new Intl.DateTimeFormat('pt-BR', {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value))
}

function TeamIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="9" cy="8" r="3" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M4 20a5 5 0 0 1 10 0" />
      <path d="M13 20a4 4 0 0 1 7 0" />
    </svg>
  )
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 7h16" />
      <path d="M9 4h6" />
      <path d="M8 7l1 13h6l1-13" />
      <path d="M10 11v5" />
      <path d="M14 11v5" />
    </svg>
  )
}

function SidebarPanelIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 5h6v6H4z" />
      <path d="M14 5h6v6h-6z" />
      <path d="M4 13h6v6H4z" />
      <path d="M14 13h6v6h-6z" />
    </svg>
  )
}

function KeyIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="9" cy="12" r="3" />
      <path d="M12 12h8" />
      <path d="M18 12v3" />
      <path d="M16 12v2" />
    </svg>
  )
}

function PhoneIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M8 2h8a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Z" />
      <path d="M10 18h4" />
    </svg>
  )
}


function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="11" cy="11" r="6" />
      <path d="m16 16 4 4" />
    </svg>
  )
}

function PlusIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 5v14" />
      <path d="M5 12h14" />
    </svg>
  )
}

function EyeIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

function PencilIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m4 20 4.5-1 10-10-3.5-3.5-10 10Z" />
      <path d="m13 6 3.5 3.5" />
    </svg>
  )
}

function RestoreIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 12a8 8 0 1 0 2.4-5.7" />
      <path d="M4 4v4h4" />
    </svg>
  )
}

function DeleteIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 7h16" />
      <path d="M9 4h6" />
      <path d="M8 7l1 13h6l1-13" />
    </svg>
  )
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m5 12 4 4 10-10" />
    </svg>
  )
}

function XIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M6 6l12 12" />
      <path d="M18 6 6 18" />
    </svg>
  )
}

function MoreIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="5" r="1.5" />
      <circle cx="12" cy="12" r="1.5" />
      <circle cx="12" cy="19" r="1.5" />
    </svg>
  )
}

function CheckboxIcon({ checked }: { checked: boolean }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <rect x="4" y="4" width="16" height="16" rx="3" />
      {checked ? <path d="m7 12 3 3 7-7" /> : null}
    </svg>
  )
}

function SortIcon({ direction }: { direction: 'asc' | 'desc' }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {direction === 'asc' ? (
        <path d="m8 14 4-4 4 4" />
      ) : (
        <path d="m8 10 4 4 4-4" />
      )}
    </svg>
  )
}

function ChevronLeftIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m15 18-6-6 6-6" />
    </svg>
  )
}

function ChevronRightIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m9 18 6-6-6-6" />
    </svg>
  )
}

function ContentIcon({ scope }: { scope: CollaboratorScope }) {
  return scope === 'trash' ? <TrashIcon /> : <TeamIcon />
}

function statusLabel(status: string, t: (key: string) => string) {
  if (status === 'active') {
    return t('collaborators.status.active')
  }

  if (status === 'inactive') {
    return t('collaborators.status.inactive')
  }

  return status
}

export function CollaboratorsPage({
  session,
  scope,
  initialCollaboratorId = null,
  onInitialCollaboratorOpen,
}: CollaboratorsPageProps) {
  const { t } = useTranslation()
  const [payload, setPayload] = useState<CollaboratorsPayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [noticeTone, setNoticeTone] = useState<PageTone>('idle')
  const [noticeMessage, setNoticeMessage] = useState('')
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    status: '',
  })
  const [sort, setSort] = useState<{ key: SortKey; direction: 'asc' | 'desc' }>({
    key: scope === 'trash' ? 'deletedAt' : 'updatedAt',
    direction: 'desc',
  })
  const [draft, setDraft] = useState<CollaboratorForm>(emptyForm)
  const [drawerMode, setDrawerMode] = useState<DrawerMode | null>(null)
  const [drawerItem, setDrawerItem] = useState<CollaboratorItem | null>(null)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [openMenuState, setOpenMenuState] = useState<OpenMenuState>(null)
  const [confirmDeleteIds, setConfirmDeleteIds] = useState<string[] | null>(null)
  const [confirmDeletePermanent, setConfirmDeletePermanent] = useState(false)
  const [confirmTrashResource, setConfirmTrashResource] = useState<{
    resource: TrashResourceKey
    ids: string[]
  } | null>(null)
  const [trashResourcePayload, setTrashResourcePayload] = useState<TrashResourcePayload | null>(null)
  const [trashResourceLoading, setTrashResourceLoading] = useState(false)
  const [trashTab, setTrashTab] = useState<TrashTab>('users')
  const [isSaving, setIsSaving] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const loadDataRequestId = useRef(0)

  const title = scope === 'trash' ? t('collaborators.trash.title') : t('collaborators.title')

  const apiFetch = async (path: string, init: RequestInit = {}) => {
    const response = await fetch(`${apiBaseUrl}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${session.session.token}`,
        'Content-Type': 'application/json',
        ...(init.headers || {}),
      },
    })

    const body = await response.json().catch(() => null)

    if (!response.ok) {
      const error = body as { code?: string; message?: string } | null
      throw new Error(error?.message || t('collaborators.error.unexpected'))
    }

    return body
  }

  const loadData = async (
    nextFilters = filters,
    nextSort = sort,
    keepSelection = false,
    showLoading = true,
    preserveNotice = false,
  ) => {
    if (showLoading) {
      setLoading(true)
    }

    const requestId = ++loadDataRequestId.current

    try {
      const params = new URLSearchParams()
      params.set('scope', scope)
      params.set('sortBy', nextSort.key)
      params.set('sortDir', nextSort.direction)

      if (nextFilters.search.trim()) {
        params.set('search', nextFilters.search.trim())
      }

      if (nextFilters.role) {
        params.set('role', nextFilters.role)
      }

      if (nextFilters.status && scope !== 'trash') {
        params.set('status', nextFilters.status)
      }

      const data = (await apiFetch(`/collaborators?${params.toString()}`)) as CollaboratorsPayload

      // A newer request was issued while this one was in flight (e.g. the
      // background poll landed between a filter change and its response) —
      // discard this stale response instead of overwriting fresher data.
      if (requestId !== loadDataRequestId.current) {
        return null
      }

      setPayload(data)
      if (!preserveNotice) {
        setNoticeTone('idle')
        setNoticeMessage('')
      }
      setOpenMenuState(null)

      if (!keepSelection) {
        setSelectedIds([])
      }

      return data
    } catch (error) {
      if (requestId !== loadDataRequestId.current) {
        return null
      }

      setNoticeTone('error')
      setNoticeMessage(error instanceof Error ? error.message : t('collaborators.error.unexpected'))
      return null
    } finally {
      if (showLoading && requestId === loadDataRequestId.current) {
        setLoading(false)
      }
    }
  }

  const getTrashResourcePath = (resource: TrashResourceKey) => {
    if (resource === 'roles') {
      return '/rules'
    }

    return `/${resource}`
  }

  const getTrashResourceLabel = (item: TrashResourceItem) => item.label || item.name || item.slug || item.id

  const getTrashResourceDescription = (item: TrashResourceItem) => {
    if (item.description) {
      return item.description
    }

    if (item.slug) {
      return item.slug
    }

    if (item.type) {
      return item.type
    }

    return item.id
  }

  const loadTrashResource = async (resource: TrashResourceKey) => {
    setTrashResourceLoading(true)

    try {
      const params = new URLSearchParams()
      params.set('scope', 'trash')
      params.set('sortBy', 'deletedAt')
      params.set('sortDir', 'desc')

      const data = (await apiFetch(`${getTrashResourcePath(resource)}?${params.toString()}`)) as TrashResourcePayload
      setTrashResourcePayload(data)
      setNoticeTone('idle')
      setNoticeMessage('')
      setOpenMenuState(null)
      setSelectedIds([])
      return data
    } catch (error) {
      setNoticeTone('error')
      setNoticeMessage(error instanceof Error ? error.message : t('collaborators.error.unexpected'))
      setTrashResourcePayload(null)
      return null
    } finally {
      setTrashResourceLoading(false)
    }
  }

  useEffect(() => {
    loadData(filters, sort)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope])

  useEffect(() => {
    if (!initialCollaboratorId || scope === 'trash') {
      return
    }

    let active = true

    const openInitialCollaborator = async () => {
      try {
        const result = (await apiFetch(`/collaborators/${initialCollaboratorId}`)) as {
          collaborator: CollaboratorItem
        }

        if (!active) {
          return
        }

        openView(result.collaborator)
        onInitialCollaboratorOpen?.()
      } catch (error) {
        if (!active) {
          return
        }

        setNoticeTone('error')
        setNoticeMessage(error instanceof Error ? error.message : t('collaborators.error.unexpected'))
        onInitialCollaboratorOpen?.()
      }
    }

    void openInitialCollaborator()

    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCollaboratorId, scope, session.session.token])

  useEffect(() => {
    let active = true

    const refresh = () => {
      if (!active || document.visibilityState !== 'visible') {
        return
      }

      void loadData(filters, sort, true, false)
    }

    const intervalId = window.setInterval(refresh, 10000)

    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', refresh)

    return () => {
      active = false
      window.clearInterval(intervalId)
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', refresh)
    }
  }, [filters, sort, scope, session.session.token])

  useEffect(() => {
    setTrashTab('users')
  }, [scope])

  useEffect(() => {
    if (scope !== 'trash' || trashTab === 'users') {
      setTrashResourcePayload(null)
      return
    }

    void loadTrashResource(trashTab)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope, trashTab])

  useEffect(() => {
    if (!noticeMessage) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      setNoticeMessage('')
      setNoticeTone('idle')
    }, 3200)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [noticeMessage])

  useEffect(() => {
    const onDocumentClick = (event: MouseEvent) => {
      if (!openMenuState) {
        return
      }

      const target = event.target as Element | null

      if (!target?.closest(`[data-menu-id="${openMenuState.id}"]`)) {
        setOpenMenuState(null)
      }
    }

    document.addEventListener('mousedown', onDocumentClick)

    return () => {
      document.removeEventListener('mousedown', onDocumentClick)
    }
  }, [openMenuState])

  const items = payload?.items || []
  const totalPages = Math.max(1, Math.ceil(items.length / ROWS_PER_PAGE))
  const safeCurrentPage = Math.min(currentPage, totalPages)
  const pageStart = (safeCurrentPage - 1) * ROWS_PER_PAGE
  const visibleItems = items.slice(pageStart, pageStart + ROWS_PER_PAGE)
  const pageNumbers = Array.from({ length: totalPages }, (_, index) => index + 1)
  const roles = payload?.catalog.roles || []
  const stats = payload?.stats || {
    total: 0,
    active: 0,
    inactive: 0,
    trash: 0,
    roles: 0,
  }
  const isTrash = scope === 'trash'
  const trashTabs: Array<{ key: TrashTab; icon: JSX.Element }> = [
    { key: 'users', icon: <TeamIcon /> },
    { key: 'spaces', icon: <SidebarPanelIcon /> },
    { key: 'roles', icon: <KeyIcon /> },
  ]
  const trashResources = stats.trashResources || {
    collaborators: stats.trash,
    spaces: 0,
    roles: 0,
    permissions: 0,
  }
  const summaryCards = isTrash
    ? [
        {
          key: 'collaborators',
          icon: <TeamIcon />,
          label: t('collaborators.trash.stats.collaborators'),
          value: trashResources.collaborators,
          hint: t('collaborators.trash.stats.collaboratorsHint'),
        },
        {
          key: 'spaces',
          icon: <PlusIcon />,
          label: t('collaborators.trash.stats.spaces'),
          value: trashResources.spaces,
          hint: t('collaborators.trash.stats.spacesHint'),
        },
        {
          key: 'roles',
          icon: <EyeIcon />,
          label: t('collaborators.trash.stats.roles'),
          value: trashResources.roles,
          hint: t('collaborators.trash.stats.rolesHint'),
        },
      ]
    : [
        {
          key: 'total',
          icon: <TeamIcon />,
          label: t('collaborators.stats.total'),
          value: stats.total,
          hint: t('collaborators.stats.totalHint'),
        },
        {
          key: 'active',
          icon: <PlusIcon />,
          label: t('collaborators.stats.active'),
          value: stats.active,
          hint: t('collaborators.stats.activeHint'),
        },
        {
          key: 'trash',
          icon: <TrashIcon />,
          label: t('collaborators.stats.trash'),
          value: stats.trash,
          hint: t('collaborators.stats.trashHint'),
        },
        {
          key: 'roles',
          icon: <EyeIcon />,
          label: t('collaborators.stats.roles'),
          value: stats.roles,
          hint: t('collaborators.stats.rolesHint'),
        },
      ]
  const isAllSelected = visibleItems.length > 0 && visibleItems.every((item) => selectedIds.includes(item.id))
  const selectedCount = selectedIds.length
  const defaultCreateRoleSlug =
    roles.find((role) => role.slug === 'collaborator')?.slug || roles[0]?.slug || ''

  useEffect(() => {
    setCurrentPage((current) => Math.min(Math.max(current, 1), totalPages))
  }, [totalPages])

  const openCreate = () => {
    if (isTrash) {
      return
    }

    setDrawerItem(null)
    setDraft({
      ...emptyForm,
      roleSlug: defaultCreateRoleSlug,
      status: 'active',
      maxDailyJourneys: 1,
    })
    setDrawerMode('create')
  }

  const openView = (item: CollaboratorItem) => {
    setDrawerItem(item)
    setDrawerMode('view')
    setOpenMenuState(null)
  }

  const openEdit = (item: CollaboratorItem) => {
    setDrawerItem(item)
    setDraft({
      name: item.name,
      email: item.email,
      roleSlug: item.roleSlug,
      status: item.status,
      maxDailyJourneys: Number(item.maxDailyJourneys) || 1,
      password: '',
      confirmPassword: '',
    })
    setDrawerMode('edit')
    setOpenMenuState(null)
  }

  const closeDrawer = () => {
    setDrawerMode(null)
    setDrawerItem(null)
    setDraft(emptyForm)
  }

  useEscapeClose(Boolean(confirmDeleteIds || confirmTrashResource || drawerMode || openMenuState), () => {
    if (confirmDeleteIds) {
      setConfirmDeleteIds(null)
      return
    }

    if (confirmTrashResource) {
      setConfirmTrashResource(null)
      return
    }

    if (drawerMode) {
      closeDrawer()
      return
    }

    setOpenMenuState(null)
  })

  const toggleSelection = (id: string) => {
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id],
    )
  }

  const toggleAll = () => {
    const visibleIds = visibleItems.map((item) => item.id)
    setSelectedIds((current) => {
      const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => current.includes(id))

      if (allVisibleSelected) {
        return current.filter((id) => !visibleIds.includes(id))
      }

      return Array.from(new Set([...current, ...visibleIds]))
    })
  }

  const performDelete = async (ids: string[], permanent = false) => {
    if (ids.length === 0) {
      return
    }

    setIsSaving(true)

    try {
      const results = await Promise.allSettled(
        ids.map((id) =>
          apiFetch(
            permanent ? `/collaborators/${id}?permanent=true` : `/collaborators/${id}`,
            {
              method: 'DELETE',
            },
          ),
        ),
      )
      const failed = results.filter((result) => result.status === 'rejected').length

      setConfirmDeleteIds(null)
      setConfirmDeletePermanent(false)

      if (failed === 0) {
        setNoticeTone('success')
        setNoticeMessage(
          permanent ? t('collaborators.success.deletedPermanent') : t('collaborators.success.deleted'),
        )
      } else if (failed < ids.length) {
        setNoticeTone('error')
        setNoticeMessage(t('collaborators.error.partial', { failed, total: ids.length }))
      } else {
        setNoticeTone('error')
        setNoticeMessage(t('collaborators.error.unexpected'))
      }

      if (failed < ids.length) {
        await loadData(filters, sort, false, true, true)
      }
    } finally {
      setIsSaving(false)
    }
  }

  const performRestore = async (ids: string[]) => {
    if (ids.length === 0) {
      return
    }

    setIsSaving(true)

    try {
      const results = await Promise.allSettled(
        ids.map((id) =>
          apiFetch(`/collaborators/${id}/restore`, {
            method: 'POST',
          }),
        ),
      )
      const failed = results.filter((result) => result.status === 'rejected').length

      if (failed === 0) {
        setNoticeTone('success')
        setNoticeMessage(t('collaborators.success.restored'))
      } else if (failed < ids.length) {
        setNoticeTone('error')
        setNoticeMessage(t('collaborators.error.partial', { failed, total: ids.length }))
      } else {
        setNoticeTone('error')
        setNoticeMessage(t('collaborators.error.unexpected'))
      }

      setSelectedIds([])

      if (failed < ids.length) {
        await loadData(filters, sort, false, true, true)
      }
    } finally {
      setIsSaving(false)
    }
  }

  const performTrashResourceRestore = async (resource: TrashResourceKey, ids: string[]) => {
    if (ids.length === 0) {
      return
    }

    setIsSaving(true)

    try {
      const results = await Promise.allSettled(
        ids.map((id) =>
          apiFetch(`${getTrashResourcePath(resource)}/${id}/restore`, {
            method: 'POST',
          }),
        ),
      )
      const failed = results.filter((result) => result.status === 'rejected').length

      if (failed === 0) {
        setNoticeTone('success')
        setNoticeMessage(t(`collaborators.trash.success.${resource}.restored`))
      } else if (failed < ids.length) {
        setNoticeTone('error')
        setNoticeMessage(t('collaborators.error.partial', { failed, total: ids.length }))
      } else {
        setNoticeTone('error')
        setNoticeMessage(t('collaborators.error.unexpected'))
      }

      setSelectedIds([])

      if (failed < ids.length) {
        await loadTrashResource(resource)
        await loadData(filters, sort, false, false, true)
      }
    } finally {
      setIsSaving(false)
    }
  }

  const performTrashResourcePermanentDelete = async () => {
    if (!confirmTrashResource || confirmTrashResource.ids.length === 0) {
      return
    }

    const { resource, ids } = confirmTrashResource
    setIsSaving(true)

    try {
      const results = await Promise.allSettled(
        ids.map((id) =>
          apiFetch(`${getTrashResourcePath(resource)}/${id}?permanent=true`, {
            method: 'DELETE',
          }),
        ),
      )
      const failed = results.filter((result) => result.status === 'rejected').length

      setConfirmTrashResource(null)

      if (failed === 0) {
        setNoticeTone('success')
        setNoticeMessage(t(`collaborators.trash.success.${resource}.deletedPermanent`))
      } else if (failed < ids.length) {
        setNoticeTone('error')
        setNoticeMessage(t('collaborators.error.partial', { failed, total: ids.length }))
      } else {
        setNoticeTone('error')
        setNoticeMessage(t('collaborators.error.unexpected'))
      }

      setSelectedIds([])

      if (failed < ids.length) {
        await loadTrashResource(resource)
        await loadData(filters, sort, false, false, true)
      }
    } finally {
      setIsSaving(false)
    }
  }

  const performDraftSave = async () => {
    if (!drawerMode) {
      return
    }

    if (draft.confirmPassword.trim() && draft.password !== draft.confirmPassword) {
      setNoticeTone('error')
      setNoticeMessage(t('collaborators.form.passwordMismatch'))
      return
    }

    if (drawerMode === 'create' && !draft.password.trim()) {
      setNoticeTone('error')
      setNoticeMessage(t('collaborators.form.passwordRequired'))
      return
    }

    const payloadBody = {
      name: draft.name,
      email: draft.email,
      roleSlug: draft.roleSlug,
      maxDailyJourneys: Math.max(1, Math.floor(Number(draft.maxDailyJourneys) || 1)),
      ...(draft.password.trim() ? { password: draft.password } : {}),
      ...(drawerMode === 'edit'
        ? {
            status: draft.status,
            maxDailyJourneys: Math.max(1, Math.floor(Number(draft.maxDailyJourneys) || 1)),
          }
        : {}),
    }

    setIsSaving(true)

    try {
      if (drawerMode === 'create') {
        await apiFetch('/collaborators', {
          method: 'POST',
          body: JSON.stringify(payloadBody),
        })
        setNoticeTone('success')
        setNoticeMessage(t('collaborators.success.created'))
      } else if (drawerMode === 'edit' && drawerItem) {
        await apiFetch(`/collaborators/${drawerItem.id}`, {
          method: 'PATCH',
          body: JSON.stringify(payloadBody),
        })
        setNoticeTone('success')
        setNoticeMessage(t('collaborators.success.updated'))
      }

      closeDrawer()
      await loadData(filters, sort, false, true, true)
    } catch (error) {
      setNoticeTone('error')
      setNoticeMessage(error instanceof Error ? error.message : t('collaborators.error.unexpected'))
    } finally {
      setIsSaving(false)
    }
  }

  const releaseDevicePin = async () => {
    if (!drawerItem) {
      return
    }

    setIsSaving(true)

    try {
      const result = (await apiFetch(`/collaborators/${drawerItem.id}/device/reset`, {
        method: 'POST',
      })) as { collaborator: CollaboratorItem }

      setDrawerItem(result.collaborator)
      setPayload((current) =>
        current
          ? {
              ...current,
              items: current.items.map((item) =>
                item.id === result.collaborator.id ? result.collaborator : item,
              ),
            }
          : current,
      )
      setNoticeTone('success')
      setNoticeMessage(t('collaborators.success.deviceReleased'))
      await loadData(filters, sort, true, false, true)
    } catch (error) {
      setNoticeTone('error')
      setNoticeMessage(error instanceof Error ? error.message : t('collaborators.error.unexpected'))
    } finally {
      setIsSaving(false)
    }
  }

  const authorizeAdditionalJourney = async (item: CollaboratorItem | null = drawerItem) => {
    if (!item) {
      return
    }

    setIsSaving(true)

    try {
      await apiFetch(`/collaborators/${item.id}/journeys/authorize`, {
        method: 'POST',
      })
      setNoticeTone('success')
      setNoticeMessage(t('collaborators.success.additionalJourney'))
      await loadData(filters, sort, true, false, true)
    } catch (error) {
      setNoticeTone('error')
      setNoticeMessage(error instanceof Error ? error.message : t('collaborators.error.unexpected'))
    } finally {
      setIsSaving(false)
    }
  }

  const submitDraft = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    await performDraftSave()
  }

  const submitSearch = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setCurrentPage(1)
    await loadData(filters, sort)
  }

  const resetSearch = async () => {
    const next = { search: '', role: '', status: '' }
    setFilters(next)
    setCurrentPage(1)
    await loadData(next, sort)
  }

  const toggleSort = async (key: SortKey) => {
    const nextSort: { key: SortKey; direction: 'asc' | 'desc' } =
      sort.key === key
        ? {
            key,
            direction: sort.direction === 'asc' ? 'desc' : 'asc',
          }
        : {
            key,
            direction: 'asc' as const,
          }

    setSort(nextSort)
    setCurrentPage(1)
    await loadData(filters, nextSort, true)
  }

  const submitBulkSoftDelete = async () => {
    requestDelete(selectedIds)
  }

  const submitBulkRestore = async () => {
    await performRestore(selectedIds)
  }

  const requestPermanentDelete = (ids: string[]) => {
    setConfirmDeletePermanent(true)
    setConfirmDeleteIds(ids)
  }

  const requestDelete = (ids: string[]) => {
    setConfirmDeletePermanent(false)
    setConfirmDeleteIds(ids)
  }

  const confirmPermanentDelete = async () => {
    if (!confirmDeleteIds || confirmDeleteIds.length === 0) {
      return
    }

    await performDelete(confirmDeleteIds, confirmDeletePermanent)
    setSelectedIds((current) => current.filter((id) => !confirmDeleteIds.includes(id)))
  }

  const setRowMenu = (id: string, buttonElement: HTMLButtonElement) => {
    const rect = buttonElement.getBoundingClientRect()
    const tableWrap = buttonElement.closest('.admin-table-wrap')
    const wrapRect = tableWrap ? tableWrap.getBoundingClientRect() : null
    const spaceBelow = wrapRect ? wrapRect.bottom - rect.bottom : window.innerHeight - rect.bottom
    const spaceAbove = wrapRect ? rect.top - wrapRect.top : rect.top
    const menuEstimate = 190
    const direction: MenuDirection =
      spaceBelow < menuEstimate && spaceAbove > spaceBelow ? 'up' : 'down'

    const coords = {
      top: rect.top,
      left: rect.left,
      right: rect.right,
      bottom: rect.bottom,
    }

    setOpenMenuState((current) => {
      if (current?.id === id) {
        return null
      }
      return { id, direction, coords }
    })
  }

  const renderSortHeader = (key: SortKey, label: string) => {
    const active = sort.key === key
    const direction = active ? sort.direction : 'asc'

    return (
      <button
        type="button"
        className={`admin-sort-button${active ? ' admin-sort-button--active' : ''}`}
        onClick={() => void toggleSort(key)}
        aria-sort={active ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <span>{label}</span>
        {active ? <SortIcon direction={direction} /> : <SortIcon direction="asc" />}
      </button>
    )
  }

  const drawerTitle =
    drawerMode === 'create'
      ? t('collaborators.drawer.newTitle')
      : drawerMode === 'edit'
        ? t('collaborators.drawer.editTitle')
        : t('collaborators.drawer.viewTitle')

  const drawerEyebrow =
    drawerMode === 'create'
      ? ''
      : drawerMode === 'edit'
        ? t('collaborators.drawer.edit')
        : ''

  return (
    <div className="admin-content-shell admin-content-shell--full">
      <header className="admin-content-header">
        <div className="admin-content-header__icon">
          <ContentIcon scope={scope} />
        </div>
        <div className="admin-content-header__copy">
          <h2>{title}</h2>
        </div>
      </header>

      {isTrash ? (
        <div className="admin-stat-grid admin-stat-grid--team">
          {summaryCards.map((card) => (
            <article key={card.key} className="admin-stat">
              <div className="admin-card__icon">{card.icon}</div>
              <span>{card.label}</span>
              <strong>{card.value}</strong>
              <small>{card.hint}</small>
            </article>
          ))}
        </div>
      ) : null}

      {isTrash ? (
        <div className="admin-tabs" role="tablist" aria-label={t('collaborators.trash.tabs.label')}>
          {trashTabs.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`admin-tabs__item${trashTab === tab.key ? ' admin-tabs__item--active' : ''}`}
              onClick={() => {
                setTrashTab(tab.key)
                setSelectedIds([])
                setOpenMenuState(null)
              }}
            >
              {tab.icon}
              {t(`collaborators.trash.tabs.${tab.key}`)}
            </button>
          ))}
        </div>
      ) : null}

      <section className="admin-panel admin-panel--team">
        {isTrash && trashTab !== 'users' ? (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>{t('collaborators.trash.table.name')}</th>
                  <th>{t('collaborators.trash.table.description')}</th>
                  <th>{t('collaborators.trash.table.status')}</th>
                  <th>{t('collaborators.trash.table.deletedAt')}</th>
                  {trashTab !== 'permissions' ? <th>{t('collaborators.trash.table.actions')}</th> : null}
                </tr>
              </thead>
              <tbody>
                {!trashResourceLoading && (trashResourcePayload?.items || []).length === 0 ? (
                  <tr>
                    <td colSpan={trashTab === 'permissions' ? 4 : 5}>
                      <div className="admin-empty-state">
                        <strong>{t('collaborators.trash.empty.title')}</strong>
                        <p>{t('collaborators.trash.empty.description')}</p>
                      </div>
                    </td>
                  </tr>
                ) : null}

                {trashResourceLoading ? (
                  <tr>
                    <td colSpan={trashTab === 'permissions' ? 4 : 5}>
                      <div className="admin-empty-state">
                        <strong>{t('common.loading')}</strong>
                      </div>
                    </td>
                  </tr>
                ) : null}

                {(trashResourcePayload?.items || []).map((item) => (
                  <tr key={item.id}>
                    <td>
                      <strong>{getTrashResourceLabel(item)}</strong>
                    </td>
                    <td>{getTrashResourceDescription(item)}</td>
                    <td>
                      <span className="admin-status admin-status--trash">
                        {t('collaborators.status.trashed')}
                      </span>
                    </td>
                    <td>{formatDateOnly(item.deletedAt)}</td>
                    {trashTab !== 'permissions' ? (
                      <td>
                        <div className="admin-row-actions" data-menu-id={item.id}>
                          <div className="admin-row-menu">
                            <button
                              data-menu-toggle
                              type="button"
                              className="admin-icon-button"
                              onClick={(event) => setRowMenu(item.id, event.currentTarget)}
                              aria-expanded={openMenuState?.id === item.id}
                              aria-haspopup="menu"
                              title={t('collaborators.actions.more')}
                            >
                              <MoreIcon />
                            </button>
                            {openMenuState?.id === item.id ? createPortal(
                              <div
                                data-menu-id={openMenuState.id}
                                className={`admin-row-menu__panel admin-row-menu__panel--${openMenuState.direction}`}
                                role="menu"
                                style={{
                                  position: 'fixed',
                                  top: openMenuState.direction === 'down' ? `${openMenuState.coords.bottom + 8}px` : 'auto',
                                  bottom: openMenuState.direction === 'up' ? `${window.innerHeight - openMenuState.coords.top + 8}px` : 'auto',
                                  left: openMenuState.coords.left < window.innerWidth / 2 ? `${openMenuState.coords.left}px` : 'auto',
                                  right: openMenuState.coords.left < window.innerWidth / 2 ? 'auto' : `${window.innerWidth - openMenuState.coords.right}px`,
                                  transform: 'none',
                                  zIndex: 9999,
                                }}
                              >
                                <button
                                  type="button"
                                  className="admin-row-menu__item"
                                  onClick={() => {
                                    setOpenMenuState(null)
                                    void performTrashResourceRestore(trashTab as TrashResourceKey, [item.id])
                                  }}
                                >
                                  <RestoreIcon />
                                  {t('collaborators.actions.restore')}
                                </button>
                                <button
                                  type="button"
                                  className="admin-row-menu__item admin-row-menu__item--danger"
                                  onClick={() => {
                                    setOpenMenuState(null)
                                    setConfirmTrashResource({
                                      resource: trashTab as TrashResourceKey,
                                      ids: [item.id],
                                    })
                                  }}
                                >
                                  <DeleteIcon />
                                  {t('collaborators.actions.deletePermanent')}
                                </button>
                              </div>,
                              document.body
                            ) : null}
                          </div>
                        </div>
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <>
        <div className="admin-panel__header admin-panel__header--team">
          <div>
            <h3>{isTrash ? t('collaborators.trash.label') : t('collaborators.list.label')}</h3>
            <p>{isTrash ? t('collaborators.trash.description') : t('collaborators.list.description')}</p>
          </div>
          {!isTrash ? (
            <button type="button" className="admin-action-button" onClick={openCreate}>
              <PlusIcon />
              {t('collaborators.actions.create')}
            </button>
          ) : null}
        </div>

        <form className="admin-search" onSubmit={submitSearch}>
          <label className="admin-search__field">
            <span>{t('collaborators.filters.search')}</span>
            <div className="admin-search__input">
              <SearchIcon />
              <input
                type="search"
                value={filters.search}
                onChange={(event) =>
                  setFilters((current) => ({ ...current, search: event.target.value }))
                }
                placeholder={t('collaborators.filters.searchPlaceholder')}
              />
            </div>
          </label>

          <label className="admin-search__field">
            <span>{t('collaborators.filters.role')}</span>
            <select
              value={filters.role}
              onChange={(event) =>
                setFilters((current) => ({ ...current, role: event.target.value }))
              }
            >
              <option value="">{t('collaborators.filters.any')}</option>
              {roles.map((role) => (
                <option key={role.slug} value={role.slug}>
                  {catalogLabel(role.slug, t)}
                </option>
              ))}
            </select>
          </label>

          {!isTrash ? (
            <label className="admin-search__field">
              <span>{t('collaborators.filters.status')}</span>
              <select
                value={filters.status}
                onChange={(event) =>
                  setFilters((current) => ({ ...current, status: event.target.value }))
                }
              >
                <option value="">{t('collaborators.filters.any')}</option>
                <option value="active">{t('collaborators.status.active')}</option>
                <option value="inactive">{t('collaborators.status.inactive')}</option>
              </select>
            </label>
          ) : (
            <div className="admin-search__field" />
          )}

          <div className="admin-search__actions">
            <button type="submit" className="admin-action-button">
              <SearchIcon />
              {t('collaborators.actions.search')}
            </button>
            <button type="button" className="admin-link-button" onClick={() => void resetSearch()}>
              <XIcon />
              {t('collaborators.actions.clear')}
            </button>
          </div>
        </form>

        {selectedCount > 0 ? (
          <div className="admin-bulkbar">
            <div className="admin-bulkbar__copy">
              <strong>
                {t('collaborators.bulk.selected', { count: selectedCount })}
              </strong>
              <span>{t('collaborators.bulk.helper')}</span>
            </div>
            <div className="admin-bulkbar__actions">
              {!isTrash ? (
                <button
                  type="button"
                  className="admin-action-button"
                  onClick={() => void submitBulkSoftDelete()}
                  disabled={isSaving}
                >
                  <TrashIcon />
                  {t('collaborators.bulk.moveToTrash')}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="admin-action-button"
                    onClick={() => void submitBulkRestore()}
                    disabled={isSaving}
                  >
                    <RestoreIcon />
                    {t('collaborators.bulk.restore')}
                  </button>
                  <button
                    type="button"
                    className="admin-action-button admin-action-button--danger"
                    onClick={() =>
                      requestPermanentDelete(selectedIds)
                    }
                    disabled={isSaving}
                  >
                    <DeleteIcon />
                    {t('collaborators.bulk.deletePermanent')}
                  </button>
                </>
              )}
              <button type="button" className="admin-link-button" onClick={() => setSelectedIds([])}>
                <XIcon />
                {t('collaborators.bulk.clearSelection')}
              </button>
            </div>
          </div>
        ) : null}

        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th className="admin-table__checkbox">
                  <button
                    type="button"
                    className="admin-checkbox-button"
                    onClick={toggleAll}
                    aria-label={t('collaborators.table.selectAll')}
                  >
                    <CheckboxIcon checked={isAllSelected} />
                  </button>
                </th>
                <th>{renderSortHeader('name', t('collaborators.table.name'))}</th>
                <th>{renderSortHeader('email', t('collaborators.table.email'))}</th>
                <th>{renderSortHeader('roleSlug', t('collaborators.table.role'))}</th>
                <th>{renderSortHeader('status', t('collaborators.table.status'))}</th>
                <th>{renderSortHeader(isTrash ? 'deletedAt' : 'updatedAt', isTrash ? t('collaborators.table.deletedAt') : t('collaborators.table.updatedAt'))}</th>
                <th>{t('collaborators.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {!loading && items.length === 0 ? (
                <tr>
                  <td colSpan={7}>
                    <div className="admin-empty-state">
                      <strong>{t('collaborators.empty.title')}</strong>
                      <p>{t('collaborators.empty.description')}</p>
                    </div>
                  </td>
                </tr>
              ) : null}

              {visibleItems.map((item) => {
                const checked = selectedIds.includes(item.id)

                return (
                  <tr key={item.id} className={checked ? 'admin-table__row--selected' : ''}>
                    <td className="admin-table__checkbox">
                      <button
                        type="button"
                        className="admin-checkbox-button"
                        onClick={() => toggleSelection(item.id)}
                        aria-label={t('collaborators.table.selectRow', { name: item.name })}
                      >
                        <CheckboxIcon checked={checked} />
                      </button>
                    </td>
                    <td>
                      <strong>{item.name}</strong>
                    </td>
                    <td>{item.email}</td>
                    <td>{catalogLabel(item.roleSlug, t)}</td>
                    <td>
                      <span className={`admin-status admin-status--${item.deletedAt ? 'trash' : item.status}`}>
                        {item.deletedAt ? t('collaborators.status.trashed') : statusLabel(item.status, t)}
                      </span>
                    </td>
                    <td>{formatDateOnly(isTrash ? item.deletedAt : item.updatedAt)}</td>
                    <td>
                      <div className="admin-row-actions" data-menu-id={item.id}>
                        <div className="admin-row-menu">
                          <button
                            data-menu-toggle
                            type="button"
                            className="admin-icon-button"
                            onClick={(event) => setRowMenu(item.id, event.currentTarget)}
                            aria-expanded={openMenuState?.id === item.id}
                            aria-haspopup="menu"
                            title={t('collaborators.actions.more')}
                          >
                            <MoreIcon />
                          </button>
                          {openMenuState?.id === item.id ? createPortal(
                            <div
                              data-menu-id={openMenuState.id}
                              className={`admin-row-menu__panel admin-row-menu__panel--${openMenuState.direction}`}
                              role="menu"
                              style={{
                                position: 'fixed',
                                top: openMenuState.direction === 'down' ? `${openMenuState.coords.bottom + 8}px` : 'auto',
                                bottom: openMenuState.direction === 'up' ? `${window.innerHeight - openMenuState.coords.top + 8}px` : 'auto',
                                left: openMenuState.coords.left < window.innerWidth / 2 ? `${openMenuState.coords.left}px` : 'auto',
                                right: openMenuState.coords.left < window.innerWidth / 2 ? 'auto' : `${window.innerWidth - openMenuState.coords.right}px`,
                                transform: 'none',
                                zIndex: 9999,
                              }}
                            >
                              {isTrash ? (
                                <>
                                  <button
                                    type="button"
                                    className="admin-row-menu__item"
                                    onClick={() => {
                                      setOpenMenuState(null)
                                      void performRestore([item.id])
                                    }}
                                  >
                                    <RestoreIcon />
                                    {t('collaborators.actions.restore')}
                                  </button>
                                  <button
                                    type="button"
                                    className="admin-row-menu__item"
                                    onClick={() => openView(item)}
                                  >
                                    <EyeIcon />
                                    {t('collaborators.actions.view')}
                                  </button>
                                  <button
                                    type="button"
                                    className="admin-row-menu__item admin-row-menu__item--danger"
                                    onClick={() => {
                                      setOpenMenuState(null)
                                      requestPermanentDelete([item.id])
                                    }}
                                  >
                                    <DeleteIcon />
                                    {t('collaborators.actions.deletePermanent')}
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    className="admin-row-menu__item"
                                    onClick={() => openView(item)}
                                  >
                                    <EyeIcon />
                                    {t('collaborators.actions.view')}
                                  </button>
                                  <button
                                    type="button"
                                    className="admin-row-menu__item"
                                    onClick={() => openEdit(item)}
                                  >
                                    <PencilIcon />
                                    {t('collaborators.actions.edit')}
                                  </button>
                                  <button
                                    type="button"
                                    className="admin-row-menu__item"
                                    onClick={() => {
                                      setOpenMenuState(null)
                                      void authorizeAdditionalJourney(item)
                                    }}
                                  >
                                    <PlusIcon />
                                    {t('collaborators.actions.additionalJourney')}
                                  </button>
                                  <button
                                    type="button"
                                    className="admin-row-menu__item admin-row-menu__item--danger"
                                    onClick={() => {
                                      setOpenMenuState(null)
                                      requestDelete([item.id])
                                    }}
                                  >
                                    <TrashIcon />
                                    {t('collaborators.actions.moveToTrash')}
                                  </button>
                                </>
                              )}
                            </div>,
                            document.body
                          ) : null}
                        </div>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        {items.length > 0 ? (
          <div className="admin-pagination" aria-label={t('collaborators.title')}>
            <div className="admin-pagination__summary">
              <span>Página {safeCurrentPage} de {totalPages}</span>
              <strong>
                {Math.min(items.length, pageStart + 1)}-{Math.min(items.length, pageStart + visibleItems.length)} de {items.length} itens
              </strong>
            </div>
            <div className="admin-pagination__nav">
              <div className="admin-pagination__actions">
                <button
                  type="button"
                  className="admin-link-button"
                  disabled={safeCurrentPage === 1}
                  onClick={() => setCurrentPage((current) => Math.max(1, current - 1))}
                >
                  <ChevronLeftIcon />
                  Anterior
                </button>
              </div>
              <div className="admin-pagination__pages" aria-label={t('collaborators.title')}>
                {pageNumbers.map((item) => (
                  <button
                    key={item}
                    type="button"
                    className={`admin-pagination__page${item === safeCurrentPage ? ' admin-pagination__page--active' : ''}`}
                    aria-current={item === safeCurrentPage ? 'page' : undefined}
                    onClick={() => setCurrentPage(item)}
                  >
                    {item}
                  </button>
                ))}
              </div>
              <div className="admin-pagination__actions admin-pagination__actions--end">
                <button
                  type="button"
                  className="admin-link-button"
                  disabled={safeCurrentPage === totalPages}
                  onClick={() => setCurrentPage((current) => Math.min(totalPages, current + 1))}
                >
                  Próxima
                  <ChevronRightIcon />
                </button>
              </div>
            </div>
          </div>
        ) : null}
          </>
        )}
      </section>

      {drawerMode ? (
        drawerMode === 'create' ? (
          <div className="admin-modal" role="presentation">
            <div className="admin-modal__surface" role="dialog" aria-modal="true">
              <div className="admin-drawer__header">
                <div>
                  <h3>{drawerTitle}</h3>
                </div>
                <button
                  type="button"
                  className="admin-icon-button admin-icon-button--bare"
                  onClick={closeDrawer}
                  aria-label={t('common.cancel')}
                >
                  <XIcon />
                </button>
              </div>

              <form className="admin-form" onSubmit={submitDraft}>
                <label>
                  <span>{t('collaborators.form.name')}</span>
                  <input
                    type="text"
                    value={draft.name}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, name: event.target.value }))
                    }
                    required
                  />
                </label>

                <label>
                  <span>{t('collaborators.form.email')}</span>
                  <input
                    type="email"
                    value={draft.email}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, email: event.target.value }))
                    }
                    required
                  />
                </label>

                <label>
                  <span>{t('collaborators.form.role')}</span>
                  <select
                    value={draft.roleSlug}
                    onChange={(event) =>
                      setDraft((current) => ({ ...current, roleSlug: event.target.value }))
                    }
                    required
                  >
                    <option value="">{t('collaborators.filters.any')}</option>
                    {roles.map((role) => (
                      <option key={role.slug} value={role.slug}>
                        {catalogLabel(role.slug, t)}
                      </option>
                    ))}
                  </select>
                </label>

                <PasswordField
                  label={t('collaborators.form.password')}
                  value={draft.password}
                  onChange={(value) => setDraft((current) => ({ ...current, password: value }))}
                  placeholder={t('collaborators.form.passwordRequired')}
                  required
                  variant="admin"
                />

                <PasswordField
                  label={t('collaborators.form.confirmPassword')}
                  value={draft.confirmPassword}
                  onChange={(value) =>
                    setDraft((current) => ({ ...current, confirmPassword: value }))
                  }
                  variant="admin"
                />

                <div className="admin-modal__actions">
                <button
                  type="button"
                  className="admin-link-button"
                  onClick={closeDrawer}
                >
                  <XIcon />
                  {t('common.cancel')}
                </button>
                  <button type="submit" className="admin-action-button" disabled={isSaving}>
                    <CheckIcon />
                    {isSaving ? t('common.loading') : t('common.save')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : (
          <div className={drawerMode === 'view' ? 'admin-modal' : 'admin-drawer'} role="presentation">
            <div
              className={
                drawerMode === 'view'
                  ? 'admin-modal__surface admin-modal__surface--view'
                  : 'admin-drawer__surface'
              }
              role="dialog"
              aria-modal="true"
            >
              <div className="admin-drawer__header">
                <div>
                  <p className="admin-drawer__eyebrow">{drawerEyebrow}</p>
                  <h3>{drawerMode === 'view' ? title : drawerItem?.name || drawerTitle}</h3>
                </div>
                <button
                  type="button"
                  className="admin-icon-button admin-icon-button--bare"
                  onClick={closeDrawer}
                  aria-label={t('common.cancel')}
                >
                  <XIcon />
                </button>
              </div>

              {drawerMode === 'view' && drawerItem ? (
                <div className="admin-modal__view">
                  <div className="admin-modal__view-grid">
                    <div className="admin-view-card">
                      <span>{t('collaborators.form.name')}</span>
                      <strong>{drawerItem.name}</strong>
                    </div>
                    <div className="admin-view-card">
                      <span>{t('collaborators.form.email')}</span>
                      <strong>{drawerItem.email}</strong>
                    </div>
                    <div className="admin-view-card">
                      <span>{t('collaborators.form.role')}</span>
                      <strong>{catalogLabel(drawerItem.roleSlug, t)}</strong>
                    </div>
                    <div className="admin-view-card">
                      <span>{t('collaborators.form.status')}</span>
                      <strong>{statusLabel(drawerItem.status, t)}</strong>
                    </div>
                    <div className="admin-view-card">
                      <span>{t('collaborators.detail.updatedAt')}</span>
                      <strong>{formatDateTime(drawerItem.updatedAt)}</strong>
                    </div>
                    <div className="admin-view-card">
                      <span>{t('collaborators.detail.deletedAt')}</span>
                      <strong>{formatDateOnly(drawerItem.deletedAt)}</strong>
                    </div>
                  </div>

                  <div className="admin-modal__view-grid admin-modal__view-grid--meta">
                    <div className="admin-view-card">
                      <span>{t('collaborators.detail.permissions')}</span>
                      <strong>{drawerItem.permissionsCount}</strong>
                    </div>
                    <div className="admin-view-card">
                      <span>{t('collaborators.detail.rules')}</span>
                      <strong>{drawerItem.rulesCount}</strong>
                    </div>
                    <div className="admin-view-card">
                      <span>{t('collaborators.device.title')}</span>
                      <strong>
                        {drawerItem.hasDevicePin
                          ? t('collaborators.device.bound')
                          : t('collaborators.device.free')}
                      </strong>
                    </div>
                    <div className="admin-view-card">
                      <span>{t('collaborators.journey.maxDailyTitle')}</span>
                      <strong>{t('collaborators.journey.maxDailyValue', { count: drawerItem.maxDailyJourneys || 1 })}</strong>
                    </div>
                  </div>

                  {drawerItem.deletedBy ? (
                    <div className="admin-view-card admin-view-card--wide">
                      <span>{t('collaborators.detail.deletedBy')}</span>
                      <strong>{drawerItem.deletedBy.name}</strong>
                    </div>
                  ) : null}

                  <div className="admin-modal__actions admin-modal__actions--view">
                    <button type="button" className="admin-link-button" onClick={closeDrawer}>
                      <XIcon />
                      {t('common.cancel')}
                    </button>
                  </div>
                </div>
              ) : (
                <form className="admin-form" onSubmit={submitDraft}>
                  <label>
                    <span>{t('collaborators.form.name')}</span>
                    <input
                      type="text"
                      value={draft.name}
                      onChange={(event) =>
                        setDraft((current) => ({ ...current, name: event.target.value }))
                      }
                      required
                    />
                  </label>

                  <label>
                    <span>{t('collaborators.form.email')}</span>
                    <input
                      type="email"
                      value={draft.email}
                      onChange={(event) =>
                        setDraft((current) => ({ ...current, email: event.target.value }))
                      }
                      required
                    />
                  </label>

                  <label>
                    <span>{t('collaborators.form.role')}</span>
                    <select
                      value={draft.roleSlug}
                      onChange={(event) =>
                        setDraft((current) => ({ ...current, roleSlug: event.target.value }))
                      }
                      required
                    >
                      <option value="">{t('collaborators.filters.any')}</option>
                      {roles.map((role) => (
                        <option key={role.slug} value={role.slug}>
                          {catalogLabel(role.slug, t)}
                        </option>
                      ))}
                    </select>
                  </label>

                  <PasswordField
                    label={t('collaborators.form.password')}
                    value={draft.password}
                    onChange={(value) => setDraft((current) => ({ ...current, password: value }))}
                    placeholder={t('collaborators.form.passwordOptional')}
                    variant="admin"
                  />

                  <PasswordField
                    label={t('collaborators.form.confirmPassword')}
                    value={draft.confirmPassword}
                    onChange={(value) =>
                      setDraft((current) => ({ ...current, confirmPassword: value }))
                    }
                    variant="admin"
                  />

                  {drawerMode === 'edit' && drawerItem ? (
                    <section className="admin-form__panel">
                      <div>
                        <span>{t('collaborators.device.title')}</span>
                        <strong>
                          {drawerItem.hasDevicePin
                            ? t('collaborators.device.bound')
                            : t('collaborators.device.free')}
                        </strong>
                        <small>{t('collaborators.device.helper')}</small>
                      </div>
                      <button
                        type="button"
                        className="admin-link-button"
                        onClick={() => void releaseDevicePin()}
                        disabled={isSaving || !drawerItem.hasDevicePin}
                      >
                        <PhoneIcon />
                        {t('collaborators.actions.releaseDevice')}
                      </button>
                    </section>
                  ) : null}

                  {drawerMode === 'edit' && drawerItem ? (
                    <section className="admin-form__panel">
                      <div>
                        <span>{t('collaborators.journey.title')}</span>
                        <strong>{t('collaborators.journey.additional')}</strong>
                        <small>{t('collaborators.journey.helper')}</small>
                      </div>
                      <button
                        type="button"
                        className="admin-link-button"
                        onClick={() => void authorizeAdditionalJourney()}
                        disabled={isSaving}
                      >
                        <PlusIcon />
                        {t('collaborators.actions.additionalJourney')}
                      </button>
                    </section>
                  ) : null}

                  <label>
                    <span>{t('collaborators.journey.maxDailyTitle')}</span>
                    <small>{t('collaborators.journey.maxDailyHelper')}</small>
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={draft.maxDailyJourneys}
                      onChange={(event) =>
                        setDraft((current) => ({
                          ...current,
                          maxDailyJourneys: Math.max(1, Math.floor(Number(event.target.value) || 1)),
                        }))
                      }
                    />
                  </label>

                  <label>
                    <span>{t('collaborators.form.status')}</span>
                    <div className="admin-status-switch" data-state={draft.status}>
                      <label className="admin-status-switch__control">
                        <input
                          type="checkbox"
                          checked={draft.status === 'active'}
                          onChange={(event) =>
                            setDraft((current) => ({
                              ...current,
                              status: event.target.checked ? 'active' : 'inactive',
                            }))
                          }
                          aria-label={t('collaborators.form.status')}
                        />
                        <span className="admin-status-switch__track">
                          <span className="admin-status-switch__thumb" />
                        </span>
                      </label>
                    </div>
                  </label>

                  <div className="admin-drawer__actions">
                    <button
                      type="button"
                      className="admin-link-button"
                      onClick={closeDrawer}
                    >
                      <XIcon />
                      {t('common.cancel')}
                    </button>
                    <button type="submit" className="admin-action-button" disabled={isSaving}>
                      <CheckIcon />
                      {isSaving ? t('common.loading') : t('common.save')}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        )
      ) : null}

      {confirmTrashResource ? (
        <div className="admin-modal" role="presentation">
          <div className="admin-modal__surface admin-modal__surface--confirm" role="dialog" aria-modal="true">
            <button
              type="button"
              className="admin-icon-button admin-icon-button--bare admin-modal__close"
              onClick={() => setConfirmTrashResource(null)}
              aria-label={t('common.close')}
            >
              <XIcon />
            </button>
            <p className="admin-modal__eyebrow">{t('collaborators.modal.deleteTitle')}</p>
            <div className="admin-modal__actions">
              <button
                type="button"
                className="admin-link-button"
                onClick={() => setConfirmTrashResource(null)}
                aria-label={t('common.cancel')}
              >
                <XIcon />
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="admin-action-button admin-action-button--danger"
                onClick={() => void performTrashResourcePermanentDelete()}
                disabled={isSaving}
              >
                <DeleteIcon />
                {isSaving ? t('common.loading') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {confirmDeleteIds ? (
        <div className="admin-modal" role="presentation">
          <div className="admin-modal__surface admin-modal__surface--confirm" role="dialog" aria-modal="true">
            <button
              type="button"
              className="admin-icon-button admin-icon-button--bare admin-modal__close"
              onClick={() => {
                setConfirmDeleteIds(null)
                setConfirmDeletePermanent(false)
              }}
              aria-label={t('common.close')}
            >
              <XIcon />
            </button>
            <p className="admin-modal__eyebrow">{t('collaborators.modal.deleteTitle')}</p>
            {confirmDeleteIds.length > 1 ? (
              <p>{t('collaborators.modal.bulkDeleteText', { count: confirmDeleteIds.length })}</p>
            ) : null}
            <div className="admin-modal__actions">
              <button
                type="button"
                className="admin-link-button"
                onClick={() => {
                  setConfirmDeleteIds(null)
                  setConfirmDeletePermanent(false)
                }}
                aria-label={t('common.cancel')}
              >
                <XIcon />
                {t('common.cancel')}
              </button>
              <button
                type="button"
                className="admin-action-button admin-action-button--danger"
                onClick={() => void confirmPermanentDelete()}
                disabled={isSaving}
              >
                <DeleteIcon />
                {isSaving ? t('common.loading') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {noticeMessage ? (
        <div className="admin-toast-stack" aria-live="polite" aria-atomic="true">
          <div className={`admin-toast admin-toast--${noticeTone}`} role="status">
            <div className="admin-toast__content">
              <strong>{noticeMessage}</strong>
            </div>
            <button
              type="button"
              className="admin-icon-button admin-icon-button--bare"
              onClick={() => {
                setNoticeMessage('')
                setNoticeTone('idle')
              }}
              aria-label={t('common.close')}
            >
              <XIcon />
            </button>
          </div>
        </div>
      ) : null}
    </div>
  )
}
